import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
// @ts-ignore
import { ContactFragment } from '@/api/ariadne/__generated_types__/graphql';
import * as userSelectors from '@/user/user-selectors';
import { LoadingPlaceholder } from '@/widgets/components/data-loading-placeholder';
import UserOutlined from '@iconify/icons-ant-design/user-outlined';
import { Icon } from '@iconify/react';
import gql from 'graphql-tag';
const defaultResources = [
  {
    title: 'Release Notes',
    titleLink: 'https://help.rentalscape.com',
    description:
      'Rentalscape is always growing and adding new features to help you more effectively manage your short-term rental program. Keep up to date with the latest changes through our Release Notes.',
  },
  {
    title: 'Registration System User Guide',
    description:
      'We provide user guides to help support your customers with using our online registration portal to register their properties, keep their information up-to-date, renew their registrations, and report and pay their taxes.',
    titleLink: 'https://help.rentalscape.com',
  },
];

const defaultSupportPeople = [
  {
    title: 'Deckard Support Team',
    intro: 'for system outages or immediate tech support',
    imageUri: 'https://via.placeholder.com/150',
    name: 'Deckard Support Team',
    email: 'support@deckard.com',
    description:
      'Our support email address is monitored 24/7 and we will generally respond to emails in under 1 hour.  Please describe your question or issue in the email, and include screenshots if appropriate.',
  },
  {
    title: 'Client Relationship Manager',
    intro: 'for contract or renewal questions',
    imageUri: 'https://via.placeholder.com/150',
    name: 'Dana Lorimer',
    phoneNumber: '858-330-4011',
    email: 'support@deckard.com',
  },
  {
    title: 'Director of Client Success',
    intro: 'for additional support as required',
    imageUri: 'https://via.placeholder.com/150',
    name: 'Melissa Mejia',
    email: 'melissa.mejia@deckard.com',
    phoneNumber: '619-370-0546',
  },
];

const GET_SUPPORT_INFORMATION = gql`
  query GetSupportInformation($regionId: String!) {
    region(regionId: $regionId) {
      support {
        contacts {
          ...Contact
        }
        resources {
          title
          titleLink
          description
          __typename
        }
      }
    }
  }

  fragment Contact on SupportContact {
    title
    intro
    imageUri
    name
    email
    phoneNumber
    description
  }
`;

const fields: { field: keyof ContactFragment; label: string }[] = [
  {
    field: 'name',
    label: 'Name',
  },
  {
    field: 'email',
    label: 'Email',
  },
  {
    field: 'phoneNumber',
    label: 'Phone Number',
  },
];

function HelpWithRentascape() {
  const regionId: string = useSelector(userSelectors.currentCity);

  const { data, loading, error } = useQuery(GET_SUPPORT_INFORMATION, {
    variables: { regionId },
  });

  if (loading) return <LoadingPlaceholder loadingText={'Loading'} />;

  const supportPeople = data?.region.support?.contacts || defaultSupportPeople;
  const resources = data?.region.support?.resources || defaultResources;

  return (
    <div className="p-10">
      <div className="font-bold text-2xl">Help with Rentalscape</div>
      <div className="flex mt-4 flex-col gap-y-12 md:flex-row md:gap-x-12">
        <div>
          <div className="text-md mt-3">
            The Deckard team is here to help you succeed. If you have any
            questions, or if you have an issue you'd like help with, please
            contact our team using the below details.
          </div>

          <div className="flex flex-col gap-y-10 py-6 mt-4">
            {supportPeople.map((person) => (
              <div key={person.name}>
                <div>
                  <div className="mb-2">
                    <span className="font-bold">{person.title}</span> -{' '}
                    {person.intro}
                  </div>
                  <div className=" bg-slate-200 rounded-lg flex p-6 gap-x-6">
                    {!person.imageUri ? (
                      <Icon
                        className="rounded-full border-4 border-solid border-orange-500 w-24 h-24"
                        icon={UserOutlined}
                      />
                    ) : (
                      <img
                        className="rounded-full border-4 border-solid border-orange-500 w-24 h-24"
                        src={person.imageUri}
                        alt={person.name}
                      />
                    )}
                    <div>
                      <table>
                        <tbody className="flex flex-col gap-y-2">
                          {fields
                            .filter((field) => person[field.field])
                            .map((field) => (
                              <tr key={field.field}>
                                <td className="font-bold md:w-24 xl:w-48">
                                  {field.label}
                                </td>
                                <td className="pl-4">{person[field.field]}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      {person.description && (
                        <div className="text-md mt-4">{person.description}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="font-bold text-xl">Other Resources</div>

          <div className="flex flex-col gap-y-6 mt-6">
            {resources.map((resource) => (
              <div key={resource.title} className="flex flex-col gap-y-2">
                <a
                  href={resource.titleLink}
                  className="font-bold text-blue-600 underline"
                >
                  {resource.title}
                </a>
                <div className="text-sm">{resource.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { HelpWithRentascape };
